// -----------------------------------------------------------------------------
// Snapshots (v0.6.0 Part 6) — boards A5 / A4.1 (slider), A5.1 (viewer), resolution log 📎.
//   * SnapshotSlider: picture card with ‹ ›, "n / m", dots; + adds a snapshot (editors), ⤢ opens the viewer.
//     Newest shown first. Drag a picture onto it or paste (Ctrl+V) while it has focus to add one.
//   * SnapshotViewer (A5.1): full-size picture on a dark overlay, thumbnails, "GF-03 · Snapshot 2 of 4 ·
//     GF-03_snapshot_02.jpg"; ← / → browse, × or Esc closes.
//     v0.7.0 (round 24 #16): "Print on PDF · n of 2" tick for the picture in view (editors; issue snapshots only)
//     and a PDF badge on the thumbnails that will print — printedIds() = the chosen ones, or none chosen → 2 newest.
//     Rule in SQL: issues_snapshot_print (0016), P3 reads issues_print_snapshots.
//     v0.7.1 (round 25 #3–#6, v070-013 / v070-015): the card and the viewer show the issue's own snapshots only (a log
//     entry's picture opens from its entry, alone); click the picture = large view; 🗑 / "🗑 Remove" removes the picture
//     shown after asking (issues_snapshot_remove, 0017); at most 4 per issue (v0.8.1: no "n of 4" chip — the limit shows only on a 5th); an orange border + label on
//     every drop area while a picture is dragged over it; the Print on PDF tick changes at once and saves behind it
//     (in order), and anyone who can view the issue may tick it.
//   * ReplaceSnapshot: "This log entry already has a snapshot — replace it?" (one per log entry).
//   * RemoveSnapshot: "Remove snapshot?" (Remove / Keep).
//   * pickImage(): opens the file picker for one picture.
// Data: issues_snapshots (0015). Pictures are shrunk + uploaded by core/files.ts uploadSnapshot, then recorded with
// issues_snapshot_add; a replaced picture's stored file is deleted afterwards.
// -----------------------------------------------------------------------------
import { useEffect, useRef, useState, type DragEvent } from "react";
import { files, rpc } from "../../../core/api/backend";
import { allImages, IMAGE_TYPES, snapshotLabel, uploadSnapshot } from "../../../core/files";
import { useEsc } from "../../../core/esc";
import { InfoIcon, Modal, toast } from "../../../core/ui/kit";
import { useWheelZoom } from "../../../core/wheelZoom";
import type { IssueSnapshots, Snapshot } from "../../../core/api/types";
import { Icon } from "../../../core/ui/icons";

/** Snapshots of one issue (skips until the issue id is known); reload after adding. */
export function useIssueSnapshots(id: string | undefined) {
  const [data, setData] = useState<IssueSnapshots | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!id) { setData(null); return; }
    let live = true;
    rpc("issues_snapshots", { id }).then((d) => live && setData(d)).catch(() => live && setData(null));
    return () => { live = false; };
  }, [id, tick]);
  return { data, reload: () => setTick((t) => t + 1) };
}

/** Signed / dev URLs for stored files, fetched once per path. */
const urlCache = new Map<string, Promise<string>>();
export function useFileUrl(path: string | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true; setUrl(null);
    if (!path) return;
    if (!urlCache.has(path)) urlCache.set(path, files.url(path).catch((e) => { urlCache.delete(path); throw e; }));
    urlCache.get(path)!.then((u) => live && setUrl(u)).catch(() => {});
    return () => { live = false; };
  }, [path]);
  return url;
}

export function pickImage(): Promise<File | null> {
  return new Promise((ok) => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = IMAGE_TYPES.join(",");
    input.onchange = () => ok(input.files?.[0] ?? null);
    input.click();
  });
}

/** Shrink, upload and record a snapshot of an issue (or of one log entry; replace = drop its current one). */
export async function addSnapshot(o: { issueId: string; projectId: string; file: File; logId?: string; replace?: boolean }): Promise<void> {
  const up = await uploadSnapshot(o.projectId, o.file);
  try {
    const r = await rpc("issues_snapshot_add", { id: o.issueId, log_id: o.logId ?? null, replace: !!o.replace, ...up });
    if (r.replaced_path) files.remove(r.replaced_path).catch(() => {});
  } catch (e) {
    files.remove(up.path).catch(() => {});                // not recorded → don't leave the file behind
    throw e;
  }
}

/** Most issue snapshots (round 25 #3; the database refuses a fifth — 0017). Log-entry pictures don't count. */
export const SNAP_MAX = 4;
export const CARD_TIP = ["Click the picture to open the large view.", "Drop or paste pictures on the card to add them — up to 4 per issue; several at once is fine.",
  "Pictures of log entries open from their entry; they are not in this card.", "🗑 removes the picture shown (asks first).",
  "1 / 3 = the picture shown, of the pictures this issue has."];
/** Label on a drop area while a picture is held over it (boards A5.3 / A5.3.1). Pure — unit-tested. */
export function dropLabel(target: "card" | "log", have: number | boolean): string {
  if (target === "log") return have ? "Drop to replace this entry’s snapshot" : "Drop to add to this entry";
  const n = Number(have);
  // v0.8.1 (round 29 #7): no "n of 4" count; the limit shows only when the card is already full
  return n >= SNAP_MAX ? `Up to ${SNAP_MAX} snapshots per issue — remove one first` : `Drop pictures to add as snapshots · room for ${SNAP_MAX - n}`;
}
/** v0.9.1 (round 32 #10): several pictures dropped at once. All go in while there is room; more than the room left → ask
 *  which (A5.3.1 Choose pictures). Pure — unit-tested. */
export function dropPlan(count: number, have: number): { room: number; ask: boolean } {
  const room = Math.max(0, SNAP_MAX - have);
  return { room, ask: count > room && room > 0 };
}
export const PICK_TIP = ["Drop up to 4 pictures at once; they are all added while the issue has room (4 snapshots per issue).",
  "More than the room left: this pop-up asks which ones; the first ones are ticked for you and the ticks stop at the room left.",
  "To make room, remove a snapshot in the viewer first.", "Pictures dropped on a log entry: one per entry, as before."];
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;
/** A5.3.1 Choose pictures: tick up to `room` of the dropped pictures. */
export function ChoosePictures({ code, have, files, onAdd, onClose }: { code: string; have: number; files: File[]; onAdd: (f: File[]) => void; onClose: () => void }) {
  const room = SNAP_MAX - have;
  const [on, setOn] = useState<number[]>(files.map((_, k) => k).slice(0, room));
  const urls = useRef<string[]>([]);
  if (urls.current.length !== files.length) urls.current = files.map((f) => URL.createObjectURL(f));
  useEffect(() => () => urls.current.forEach((u) => URL.revokeObjectURL(u)), []);
  const flip = (k: number) => setOn((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : cur.length < room ? [...cur, k].sort((a, b) => a - b) : cur));
  return (
    <Modal title="Choose pictures" info={PICK_TIP} onClose={onClose} className="choose-pics natural">
      {/* v0.9.2 (round 33 #8, board A5.3.1) */}
      <p data-testid="pick-room">Only <b>{plural(room, "more snapshot")}</b> allowed on <b>{code}</b> (up to {SNAP_MAX} per issue) — tick which of the {files.length} pictures to add.</p>
      <div className="pick-grid">{files.map((f, k) => (
        <label key={k} className={`pick-tile${on.includes(k) ? " on" : ""}${!on.includes(k) && on.length >= room ? " full" : ""}`} data-pick={k + 1}>
          <span className="pick-img"><img src={urls.current[k]} alt="" /><input type="checkbox" checked={on.includes(k)} aria-label={`Add ${f.name}`} onChange={() => flip(k)} /></span>
          <span className="pick-name ellipsis" title={f.name}>{f.name}</span>
        </label>))}</div>
      <div className="modal-foot"><span className="muted small grow">{on.length} of {room} chosen</span>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn primary" disabled={!on.length} onClick={() => onAdd(on.map((k) => files[k]))}>Add {plural(on.length, "picture")}</button></div>
    </Modal>
  );
}
/** Drag-over state that ignores moves between the area's own children (no flicker). */
export function useDropOver(enabled: boolean) {
  const [over, setOver] = useState(false);
  const has = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");
  return {
    over,
    clear: () => setOver(false),
    bind: {
      onDragEnter: (e: DragEvent) => { if (enabled && has(e)) { e.preventDefault(); setOver(true); } },
      onDragOver: (e: DragEvent) => { if (enabled && has(e)) { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; if (!over) setOver(true); } },
      onDragLeave: (e: DragEvent) => { if (!(e.currentTarget as Element).contains(e.relatedTarget as Node | null)) setOver(false); },
    },
  };
}

/** A5 picture card. `snaps` = the issue's own snapshots, oldest first (log-entry pictures are left out by the caller). */
export function SnapshotSlider({ code, snaps, canEdit, onAdd, onOpen, onRemove, className = "", expand = true, loading = false }: {
  code: string; snaps: Snapshot[]; canEdit: boolean; /** v0.9.1: one or more pictures */ onAdd: (f: File[]) => void; onOpen: (index: number) => void;
  /** editors: 🗑 asks, then removes the picture shown */ onRemove?: (s: Snapshot) => void; className?: string; expand?: boolean;
  /** still fetching the list: show an empty card, not "No snapshot yet" */ loading?: boolean;
}) {
  const order = [...snaps].reverse();                       // newest first
  const [i, setI] = useState(0);
  const drop = useDropOver(canEdit);
  useEffect(() => setI(0), [snaps.length]);
  const cur = order[Math.min(i, order.length - 1)];
  const url = useFileUrl(cur?.path);
  const step = (d: number) => setI((x) => (x + d + order.length) % order.length);
  const n = order.length, full = n >= SNAP_MAX;
  const [choosing, setChoosing] = useState<File[] | null>(null);
  const add = (fs: File[]) => {
    if (!fs.length || !canEdit) return;
    if (full) { toast(`Up to ${SNAP_MAX} snapshots per issue — remove one first`); return; }
    if (dropPlan(fs.length, n).ask) setChoosing(fs); else onAdd(fs);
  };
  return (
    <div className={`snap-card slider${drop.over ? (full ? " over full" : " over") : ""} ${className}`} data-testid="snap-slider" data-snapshots={n} tabIndex={canEdit ? 0 : -1}
      aria-label={`Snapshots of ${code}`} data-drop="card" {...drop.bind}
      onDrop={(e) => { e.preventDefault(); drop.clear(); add(allImages(e.dataTransfer)); }}
      onPaste={(e) => { const fs = allImages(e.clipboardData); if (fs.length && canEdit) { e.preventDefault(); add(fs); } }}
      onKeyDown={(e) => { if (n > 1 && (e.key === "ArrowLeft" || e.key === "ArrowRight")) { e.stopPropagation(); e.preventDefault(); step(e.key === "ArrowLeft" ? -1 : 1); } }}>
      {loading ? null : n === 0
        ? <span className="snap-empty">{canEdit ? "No snapshot yet — + adds one, or drop / paste a picture here" : "No snapshot yet"}</span>
        : <>
          {url ? <img src={url} alt={snapshotLabel(code, n - Math.min(i, n - 1))} title="Click to open the large view" onClick={() => onOpen(snaps.indexOf(cur))} /> : <span className="snap-empty">Loading…</span>}
          <span className="snap-count" data-testid="snap-count">{Math.min(i, n - 1) + 1} / {n}</span>
          {n > 1 && <>
            <button type="button" className="snap-arrow left" aria-label="Previous snapshot" onClick={() => step(-1)}><Icon name="chevron_left" /></button>
            <button type="button" className="snap-arrow right" aria-label="Next snapshot" onClick={() => step(1)}><Icon name="chevron_right" /></button>
            <div className="snap-dots">{order.map((s, k) => <span key={s.id} className={k === i ? "on" : ""} />)}</div>
          </>}
        </>}
      {drop.over && <span className="drop-label" data-testid="drop-label">{dropLabel("card", n)}</span>}
      <div className="snap-tools">
        {canEdit && <button type="button" className="btn icon" title={full ? `Up to ${SNAP_MAX} snapshots per issue — remove one first` : "Add snapshot"} aria-label="Add snapshot to the issue" disabled={full}
          onClick={async () => { const f = await pickImage(); if (f) add([f]); }}>+</button>}
        {canEdit && onRemove && n > 0 && <button type="button" className="btn icon" title="Remove this picture" aria-label="Remove this snapshot" onClick={() => onRemove(cur)}><Icon name="delete" /></button>}
        {canEdit && <span className="snap-chip" data-testid="snap-chip"><InfoIcon tip={CARD_TIP} /></span>}
        {expand && n > 0 && <button type="button" className="btn icon" title="Expand snapshots" aria-label="Expand snapshots" onClick={() => onOpen(snaps.indexOf(cur))}><Icon name="open_in_full" /></button>}
      </div>
      {choosing && <ChoosePictures code={code} have={n} files={choosing} onClose={() => setChoosing(null)} onAdd={(fs) => { setChoosing(null); onAdd(fs); }} />}
    </div>
  );
}

/** Most snapshots printed in the P3 picture card (round 24 #15). */
export const PRINT_MAX = 2;
/** Which snapshots print on the PDF issue page: the chosen issue snapshots, or none chosen → the 2 newest. Pure — unit-tested. */
export function printedIds(snaps: Pick<Snapshot, "id" | "log_id" | "print">[]): Set<string> {
  const own = snaps.filter((x) => !x.log_id);                         // oldest first, as listed
  const chosen = own.filter((x) => x.print);
  return new Set((chosen.length ? chosen : own.slice(-PRINT_MAX)).map((x) => x.id));
}
export const PRINT_TIP = ["Up to 2 snapshots print on the PDF issue page; their thumbnails show PDF.",
  "Tick Print on PDF on the one you are viewing to choose it; with 2 already chosen, untick one first.", "Nothing chosen: the 2 newest print.",
  "Anyone who can view the issue can choose."];
export const REMOVE_TIP = ["Removes the picture you are viewing, after asking.", "Editors only; the picture is deleted from storage."];

/** Print choices with the ticks not yet saved laid over them (round 25 #5: the tick changes at once). Pure — unit-tested. */
export function withPending<T extends Pick<Snapshot, "id" | "print">>(snaps: T[], pending: Record<string, boolean>): T[] {
  return snaps.map((x) => (x.id in pending ? { ...x, print: pending[x.id] } : x));
}

/** A5.1 Snapshot viewer. `start` = index in `snaps` (oldest first, as numbered "Snapshot n"). From the card: the issue's own
 *  snapshots; from a log entry: that entry's one picture (`logEntry` = its date for the header).
 *  onPrint: tick / untick "Print on PDF" — the tick shows at once, saves run one after another, then `snaps` reloads.
 *  onRemove (editors): "🗑 Remove" asks first. */
export function SnapshotViewer({ code, snaps, start, onClose, onPrint, onRemove, logEntry }: { code: string; snaps: Snapshot[]; start: number; onClose: () => void;
  onPrint?: (s: Snapshot, on: boolean) => Promise<void>; onRemove?: (s: Snapshot) => Promise<void>; logEntry?: string }) {
  const [i, setI] = useState(Math.max(0, start));
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [err, setErr] = useState("");
  const [asking, setAsking] = useState(false);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const shown = withPending(snaps, pending);
  const s = shown[Math.min(i, shown.length - 1)];
  const printed = printedIds(shown);
  const chosen = shown.filter((x) => !x.log_id && x.print).length;
  const url = useFileUrl(s?.path);
  const box = useRef<HTMLDivElement>(null);
  const pic = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  useEffect(() => setZoom(1), [i]);
  useWheelZoom(pic, (k) => setZoom((z) => Math.max(1, Math.min(6, z * k))));
  // a saved tick that the reloaded list now shows is no longer pending
  useEffect(() => setPending((p) => { const left = Object.fromEntries(Object.entries(p).filter(([id, on]) => snaps.find((x) => x.id === id)?.print !== on)); return Object.keys(left).length === Object.keys(p).length ? p : left; }), [snaps]);
  useEffect(() => { if (i >= snaps.length && snaps.length) setI(snaps.length - 1); }, [snaps.length, i]);
  useEsc("popup", () => { if (asking) setAsking(false); else onClose(); return true; });
  useEffect(() => {
    box.current?.focus();
    const on = (e: KeyboardEvent) => {
      if (asking) return;
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") { e.preventDefault(); e.stopPropagation(); setI((x) => (x + (e.key === "ArrowLeft" ? -1 : 1) + snaps.length) % snaps.length); }
    };
    window.addEventListener("keydown", on, true);
    return () => window.removeEventListener("keydown", on, true);
  }, [snaps.length, asking]);
  if (!s) return null;
  const tick = (on: boolean) => {
    if (!onPrint) return;
    const target = s;
    setErr(""); setPending((p) => ({ ...p, [target.id]: on }));
    queue.current = queue.current.then(() => onPrint(target, on)).catch((x) => {
      setErr((x as Error).message); setPending((p) => { const { [target.id]: _drop, ...rest } = p; return rest; });
    });
  };
  const n = snaps.length;
  return (
    <div className="snap-viewer" role="dialog" aria-label="Snapshot viewer" ref={box} tabIndex={-1} data-testid="snap-viewer">
      <div className="row between">
        <p><span className="sv-code">{code}</span> · {logEntry ? <>Log entry {logEntry}</> : <>Snapshot {i + 1} of <span data-snapshots={`${code}:${n}`}>{n}</span></>} · <span className="sv-name">{s.name && logEntry ? s.name : snapshotLabel(code, i + 1)}</span></p>
        <div className="row gap12">
          {err && <span className="sv-err" role="alert">{err}</span>}
          {!s.log_id && (onPrint
            ? <label className="sv-print" data-print-pick={`${code}:${chosen}`}>
                <input type="checkbox" checked={s.print} disabled={!s.print && chosen >= PRINT_MAX} onChange={(e) => tick(e.target.checked)} />
                <span>Print on PDF</span><span className="sv-print-n">· {chosen} of {PRINT_MAX}</span>
              </label>
            : printed.has(s.id) && <span className="sv-print ro">Prints on PDF</span>)}
          {!s.log_id && <span className="sv-info"><InfoIcon tip={PRINT_TIP} /></span>}
          {onRemove && <span className="row gap6"><button className="btn sv-btn" data-testid="sv-remove" onClick={() => setAsking(true)}><Icon name="delete" /> Remove</button><span className="sv-info"><InfoIcon tip={REMOVE_TIP} /></span></span>}
          <button className="btn icon sv-btn" aria-label="Close" title="Close (Esc)" onClick={onClose}><Icon name="close" /></button>
        </div>
      </div>
      <div className="sv-main">
        <button className="sv-arrow" aria-label="Previous snapshot" disabled={n < 2} onClick={() => setI((i - 1 + n) % n)}><Icon name="chevron_left" /></button>
        <div className="sv-picture" ref={pic}>{url ? <img src={url} alt={snapshotLabel(code, i + 1)} style={zoom > 1 ? { transform: `scale(${zoom})` } : undefined} /> : <span>Loading…</span>}</div>
        <button className="sv-arrow" aria-label="Next snapshot" disabled={n < 2} onClick={() => setI((i + 1) % n)}><Icon name="chevron_right" /></button>
      </div>
      <div className="row between">
        <div className="sv-thumbs">{shown.map((t, k) => <Thumb key={t.id} path={t.path} on={k === i} pdf={printed.has(t.id)} onClick={() => setI(k)} label={`Snapshot ${k + 1}`} />)}</div>
        <p className="sv-hint">← → to browse · Ctrl + wheel zooms · <Icon name="close" /> or <b>Esc</b> to close</p>
      </div>
      {asking && <RemoveSnapshot onKeep={() => setAsking(false)} onRemove={async () => {
        setAsking(false);
        try { await onRemove!(s); if (n <= 1) onClose(); } catch (x) { setErr((x as Error).message); }
      }} />}
    </div>
  );
}

/** "Remove snapshot?" (round 25 #3). Esc / Keep = nothing changes. */
export function RemoveSnapshot({ onRemove, onKeep }: { onRemove: () => void; onKeep: () => void }) {
  useEsc("popup", () => { onKeep(); return true; });
  return (
    <div className="modal-backdrop top" onMouseDown={(e) => e.target === e.currentTarget && onKeep()}>
      <div className="modal small" role="alertdialog" aria-label="Remove snapshot?">
        <h2>Remove snapshot?</h2>
        <p className="muted">The picture is deleted from the issue and from storage. This can't be undone.</p>
        <div className="modal-foot"><button className="btn" onClick={onKeep}>Keep</button><button className="btn danger" onClick={onRemove}>Remove</button></div>
      </div>
    </div>
  );
}

/** Remove a snapshot, then delete its stored file (0017 issues_snapshot_remove returns the object name). */
export async function removeSnapshot(id: string): Promise<void> {
  const r = await rpc("issues_snapshot_remove", { id });
  if (r.path) files.remove(r.path).catch(() => {});
}

function Thumb({ path, on, pdf, onClick, label }: { path: string; on: boolean; pdf: boolean; onClick: () => void; label: string }) {
  const url = useFileUrl(path);
  return <button type="button" className={`sv-thumb${on ? " on" : ""}`} aria-label={label + (pdf ? " (prints on PDF)" : "")} aria-current={on || undefined}
    data-prints={pdf ? "1" : undefined} onClick={onClick}>{url && <img src={url} alt="" />}{pdf && <span className="sv-pdf">PDF</span>}</button>;
}

/** "Replace snapshot?" — a log entry keeps one snapshot. Esc = keep the current one. */
export function ReplaceSnapshot({ onReplace, onKeep }: { onReplace: () => void; onKeep: () => void }) {
  useEsc("popup", () => { onKeep(); return true; });
  return (
    <div className="modal-backdrop top" onMouseDown={(e) => e.target === e.currentTarget && onKeep()}>
      <div className="modal small" role="alertdialog" aria-label="Replace snapshot?">
        <h2>Replace snapshot?</h2>
        <p className="muted">This log entry already has a snapshot. Each entry keeps one; the current picture will be deleted.</p>
        <div className="modal-foot"><button className="btn" onClick={onKeep}>Keep current</button><button className="btn primary" onClick={onReplace}>Replace</button></div>
      </div>
    </div>
  );
}
